import React, { useState, useEffect, useRef, ReactNode, useMemo, memo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Row, Col, Spin, Progress, Typography, Button } from 'antd';
import ReactMarkdown from 'react-markdown';
import './PreviewAndResult.css'; // 自定义样式文件
import { renderAsync } from 'docx-preview';
import { DownloadOutlined } from '@ant-design/icons';

// Set PDF.js worker path
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const { Text } = Typography;

interface PreviewAndResultProps {
  file: File | string | null;
  auditResults: Record<string, string> [];
  auditProgress: number;
  downloadUrl?: File | string | null;
  downloadName?: string;
}

// Hook to dynamically calculate container width
const useContainerWidth = (ref: React.RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState(0);

  const updateWidth = () => {
    if (ref.current) {
      setWidth(ref.current.offsetWidth);
    } else {
      setWidth(0); // 默认宽度为 0，如果 ref 未初始化
    }
  };

  useEffect(() => {
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [ref]);

  return width;
};

// PDF 预览子组件，使用 React.memo 避免不必要渲染
interface PdfPreviewProps {
  file: File | string | null;
  downloadUrl?: File | string | null;
  downloadName?: string;
}

const dealWithBase64File = (file: File | string | null) => {
  if (!file) return null;
    // 如果是 File 实例
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    // 如果是 base64 字符串（例如：data:application/pdf;base64,...）
    if (typeof file === 'string' && file.startsWith('data:')) {
      // 将 base64 转为 Blob，再转为 Object URL
      const arr = file.split(',');
      const mimeMatch = arr[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const bstr = atob(arr[1]);
      const n = bstr.length;
      const u8arr = new Uint8Array(n);
      for (let i = 0; i < n; ++i) {
        u8arr[i] = bstr.charCodeAt(i);
      }
      const blob = new Blob([u8arr], { type: mime });
      const url = URL.createObjectURL(blob);
      return url;
    }

    return null;
}
/**
 * @description 判断文件是否为PDF
 */
const getFileTypeIsPdf = (base64: string): boolean => {
  // 去除前缀，如 data:application/pdf;base64,
  const cleaned = base64.replace(/^data:.*?;base64,/, '');
  const bytes = atob(cleaned).slice(0, 8); // 读取前8个字节
  const hex = Array.from(bytes).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join(' ');
  if (bytes.startsWith('%PDF') || bytes.startsWith('%pdf')) return true
  if (hex.startsWith('d0 cf 11 e0'))return false
  if (hex.startsWith('50 4b 03 04')) return false
  return true;
}

export const PdfPreview: React.FC<PdfPreviewProps> = memo(({ file, downloadUrl, downloadName }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pageWidthPt, setPageWidthPt] = useState<number | null>(null); // 存储 PDF 页面宽度
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerWidth = useContainerWidth(containerRef);
  const [isPdf, setIsPdf] = useState<boolean>(file ? file instanceof File ? ((file.name.endsWith('.pdf') || file.name.endsWith('.PDF'))) : getFileTypeIsPdf(file) : true);
  const docContainerRef = useRef<HTMLDivElement | null>(null);
  

  // 仅在 file 变化时更新 PDF 文件
  const pdfFile = useMemo(() => {
    return dealWithBase64File(file);
  }, [file]);

  // 清理 URL 对象以避免内存泄漏
  useEffect(() => {
    return () => {
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
    };
  }, [pdfFile]);
  const dealWordFile = async (file: File | string | null, docContainerRef: React.RefObject<HTMLDivElement>) => {
    let f: any = '';
    if (typeof file === 'string') {
      const response = await fetch(file);
      const blob = await response.blob();
      f = await blob.arrayBuffer();
    } else {
      f = file;
    }
    debugger
    renderAsync(f, docContainerRef.current, undefined, {
      debug: false,
      className: 'docx', // 可用于自定义样式
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      ignoreFonts: false,
    });
  }
  useEffect(() => {
    const isPdf = (file ? file instanceof File ? ((file.name.endsWith('.pdf') || file.name.endsWith('.PDF'))) : getFileTypeIsPdf(file) : true);
    if (file) {
      setIsPdf(isPdf);
    }
    if (file && !isPdf && docContainerRef.current) {
      dealWordFile(file, docContainerRef as any);
    }
  }, [file])


  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(null);
    console.log('PDF loaded successfully with', numPages, 'pages');
  };

  const onPageLoadSuccess = (page: any) => {
    // 获取 PDF 页面宽度（单位：pt）
    if (page && page._pageInfo && page._pageInfo.view) {
      setPageWidthPt(page._pageInfo.view[2]); // view[2] 是页面宽度
    }
  };

  const onDocumentLoadError = (error: Error) => {
    setError(`PDF 加载失败: ${error.message}`);
    console.log('PDF load error:', error);
  };

  // 错误边界组件
  class PdfErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
    state = { hasError: false };

    static getDerivedStateFromError(error: Error) {
      return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
      console.error('PDF rendering error caught:', error, errorInfo);
    }

    render() {
      if (this.state.hasError) {
        return <p style={{ color: 'red', padding: '10px' }}>PDF 渲染错误，请重试或联系管理员。</p>;
      }
      return this.props.children;
    }
  }

  const calculateScale = () => {
    if (!containerWidth || !pageWidthPt) return 1.0;
    const scale = containerWidth / pageWidthPt;
    // 限制 scale 范围为 0.5 到 1.5，避免过大或过小
    return Math.min(Math.max(scale, 0.5), 1);
  };
  const downloadFile = () => {
    const url = dealWithBase64File(downloadUrl || '');
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName || `已审报告${new Date().toLocaleString().replaceAll('/', '-').replaceAll(':', '')}`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  }
  return (
    <div ref={containerRef} className="pdf-preview-container">
      <h3>报告预览{!!downloadUrl && <Button onClick={downloadFile} type='primary' icon={<DownloadOutlined/>} style={{float: 'right'}}>下载已审报告</Button>}</h3>
      {file ? (
        <>
          <div hidden={!isPdf}>
          <PdfErrorBoundary>
            <Document
              file={pdfFile}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<Spin />}
            >
              {Array.from(new Array(numPages || 0), (_, index) => (
                <div key={`page_${index + 1}`} className="pdf-page-wrapper">
                  <Page
                    pageNumber={index + 1}
                    width={containerWidth}
                    scale={calculateScale()}
                    onLoadSuccess={onPageLoadSuccess}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={<Spin />}
                  />
                </div>
              ))}
            </Document>
          </PdfErrorBoundary>
          </div>
         <div ref={docContainerRef} hidden={!!isPdf}></div>
        </>
      ) : error ? (
        <p style={{ color: 'red', textAlign: 'center', padding: '20px' }}>{error}</p>
      ) : (
        <p style={{ textAlign: 'center', padding: '20px' }}>请上传报告以查看预览</p>
      )}
    </div>
  );
});

// 主组件
const PreviewAndResult: React.FC<PreviewAndResultProps> = ({ file, auditResults, auditProgress, downloadUrl, downloadName }) => {
  const resultListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (resultListRef.current) {
      resultListRef.current.scrollTop = resultListRef.current.scrollHeight;
    }
  }, [auditResults]);

  // 校验并处理每个结果，确保是有效字符串
  const getSafeMarkdownContent = (content: string | null | undefined): string => {
    // debugger
    if (typeof content !== 'string' || content.trim() === '') {
      console.warn('Invalid Markdown content detected, using fallback:', content);
      return '内容加载失败，请联系管理员。';
    }
    return content.trim();
  };

  return (
    <Row gutter={4}>
      <Col span={16}>
        <PdfPreview file={file} downloadUrl={downloadUrl || ''} downloadName={downloadName}/>
      </Col>
      <Col span={8}>
        <div style={{ border: '1px solid #d9d9d9', padding: '10px' }}>
          <h3>审核结果</h3>
          <Progress percent={auditProgress * 100} style={{ marginBottom: '10px' }} />
          <div
            ref={resultListRef}
            style={{ border: '1px solid #d9d9f9', padding: '10px', height: '400px', overflowY: 'auto' }}
            className="result-list"
          >
            {auditResults.length > 0 ? (
              auditResults.map((result, index) => (
                <div key={index} className="result-item">
                  <ErrorBoundary>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {/* {new Date().toLocaleTimeString()} */}
                      {result.resultDateTime}
                    </Text>
                    {!!result.result && <ReactMarkdown>{getSafeMarkdownContent(result.result || '')}</ReactMarkdown>}
                  </ErrorBoundary>
                </div>
              ))
            ) : (
              <p style={{ textAlign: 'center', padding: '20px' }}>暂无审核结果</p>
            )}
          </div>
        </div>
      </Col>
    </Row>
  );
};

// 错误边界组件，捕获 Markdown 渲染错误
class ErrorBoundary extends React.Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Rendering error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <p style={{ color: 'red', padding: '10px' }}>渲染错误，请联系管理员。</p>;
    }
    return this.props.children;
  }
}

export default PreviewAndResult;